import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Circle, ArrowDownCircle, ArrowUpCircle, ClipboardCheck } from 'lucide-react';
import { tresorerieApi } from '../../services/tresorerieApi';
import { formatCurrency } from '../../utils/formatters';

const num = (s: string | number | null | undefined) => Number(s ?? 0);

const OperationsRapprochement: React.FC = () => {
  const queryClient = useQueryClient();
  const [compteId, setCompteId] = useState<number | null>(null);
  const [soldeReleve, setSoldeReleve] = useState('');
  const [dateFin, setDateFin] = useState<string>(() => new Date().toISOString().slice(0, 10));

  const { data: comptes = [] } = useQuery({
    queryKey: ['tresorerie', 'comptes'],
    queryFn: () => tresorerieApi.getComptes(),
  });

  useEffect(() => {
    if (compteId === null && comptes.length > 0) setCompteId(comptes[0].id);
  }, [comptes, compteId]);

  const { data: operations = [] } = useQuery({
    queryKey: ['tresorerie', 'operations', compteId],
    queryFn: () => tresorerieApi.getOperations({ compte: compteId! }),
    enabled: compteId !== null,
  });

  const compte = comptes.find((c) => c.id === compteId);

  const pointer = useMutation(
    (id: number) => tresorerieApi.pointerOperation(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tresorerie', 'operations', compteId]);
        queryClient.invalidateQueries(['tresorerie', 'comptes']);
        queryClient.invalidateQueries(['tresorerie', 'position']);
      },
    },
  );

  const creerRapp = useMutation(
    () => tresorerieApi.createRapprochement({ compte: compteId!, date_fin: dateFin, solde_releve: Number(soldeReleve) }),
    {
      onSuccess: async (rapp) => {
        await tresorerieApi.validerRapprochement(rapp.id);
        setSoldeReleve('');
        queryClient.invalidateQueries(['tresorerie', 'operations', compteId]);
        queryClient.invalidateQueries(['tresorerie', 'comptes']);
        queryClient.invalidateQueries(['tresorerie', 'position']);
      },
    },
  );

  const ecart = compte ? Number(soldeReleve || 0) - num(compte.solde_courant) : 0;

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm font-medium text-gray-700">Compte</label>
        <select value={compteId ?? ''} onChange={(e) => setCompteId(Number(e.target.value))}
          className="border border-gray-200 rounded-xl px-3 py-2 text-gray-700">
          {comptes.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
        </select>
        {compte && (
          <span className="text-sm text-gray-500">
            Solde courant : <span className="font-semibold text-gray-900">{formatCurrency(num(compte.solde_courant))}</span>
            {' · '}rapproché : <span className="font-medium">{formatCurrency(num(compte.solde_rapproche))}</span>
          </span>
        )}
      </div>

      {/* Bloc rapprochement */}
      {compte && (
        <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Date du relevé</label>
            <input type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)}
              className="border border-gray-200 rounded-xl px-3 py-2 text-gray-700" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Solde du relevé bancaire</label>
            <input type="number" value={soldeReleve} onChange={(e) => setSoldeReleve(e.target.value)}
              placeholder="0" className="border border-gray-200 rounded-xl px-3 py-2 text-gray-700" />
          </div>
          {soldeReleve !== '' && (
            <div className={`text-sm font-medium ${ecart === 0 ? 'text-green-600' : 'text-amber-600'}`}>
              Écart : {formatCurrency(ecart)}
            </div>
          )}
          <button
            disabled={soldeReleve === '' || creerRapp.isLoading}
            onClick={() => creerRapp.mutate()}
            className="inline-flex items-center gap-1.5 bg-gray-800 hover:bg-gray-900 text-white text-sm font-medium px-3 py-2 rounded-lg disabled:opacity-50 ml-auto">
            <ClipboardCheck className="h-4 w-4" />
            {creerRapp.isLoading ? 'Validation…' : 'Valider le rapprochement'}
          </button>
        </div>
      )}

      {/* Liste des opérations */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Opérations ({operations.length})</h3>
          <p className="text-xs text-gray-400">Cochez pour pointer une opération apparaissant sur le relevé bancaire.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                <th className="px-4 py-2">Pointé</th>
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Libellé</th>
                <th className="px-4 py-2">Mode</th>
                <th className="px-4 py-2">Réf.</th>
                <th className="px-4 py-2 text-right">Montant</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {operations.map((op) => {
                const isIn = op.sens === 'credit';
                return (
                  <tr key={op.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <button onClick={() => pointer.mutate(op.id)} className="text-gray-400 hover:text-green-600">
                        {op.rapproche ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <Circle className="h-5 w-5" />}
                      </button>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">{op.date_operation}</td>
                    <td className="px-4 py-2 text-sm text-gray-800">{op.libelle}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{op.mode_operation_display}</td>
                    <td className="px-4 py-2 text-sm text-gray-500">{op.facture_numero || op.reference || '—'}</td>
                    <td className={`px-4 py-2 text-sm text-right font-semibold ${isIn ? 'text-green-700' : 'text-red-700'}`}>
                      <span className="inline-flex items-center gap-1 justify-end">
                        {isIn ? <ArrowDownCircle className="h-3.5 w-3.5" /> : <ArrowUpCircle className="h-3.5 w-3.5" />}
                        {formatCurrency(num(op.montant))}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {operations.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Aucune opération sur ce compte.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OperationsRapprochement;
